import { orderBy } from 'lodash';
import type { Moment, MomentBuiltinFormat } from 'moment';
import moment from 'moment';

interface DropdownOption {
	value: string | number;
	label: string;
	[key: string]: unknown;
}

interface DateCategory {
	value: string;
	content: Record<string, unknown>[];
}

interface CookieOptions {
	path?: string;
	domain?: string;
}

interface DateEvent {
	created_at?: string;
	[key: string]: unknown;
}

const queryParams = (object: Record<string, string>): string =>
	new URLSearchParams(object).toString();

export const generateRandomString = (): string =>
	Math.random().toString(36).substring(2, 15) +
	Math.random().toString(36).substring(2, 15);

export const generateDropdownOptions = (
	data: Record<string, unknown>[] | null | undefined,
	valueParam: string,
	labelParam: string,
): DropdownOption[] =>
	(data || []).map((item) => ({
		...item,
		value: item[valueParam] as string | number,
		label: item[labelParam] as string,
	}));

export const generateDropdownOptionsFromHash = (
	data: Record<string, string> | null | undefined,
): DropdownOption[] =>
	Object.keys(data || {}).map((key) => ({
		value: key,
		label: (data as Record<string, string>)[key],
	}));

export const convertArrayToObject = <T>(
	array: T[],
	key: keyof T,
): Record<string, T> => {
	const initialValue: Record<string, T> = {};
	return (array || []).reduce(
		(obj: Record<string, T>, item: T) => ({
			...obj,
			[item[key] as string]: item,
		}),
		initialValue,
	);
};

export function logHere(...args: unknown[]): void {
	if (
		process.env.NODE_ENV === 'development' ||
		process.env.NEXT_PUBLIC_APP_ENV === 'local'
	) {
		// biome-ignore lint/suspicious/noConsole: necessary logging
		console.log(...args);
	}
}

export function openInANewTab(url: string): void {
	const win = window.open(url, '_blank');
	if (win != null) {
		win.focus();
	}
}

export const capitalize = (string: string): string =>
	string.charAt(0).toUpperCase() + string.slice(1);

export const getKeyByValue = (
	object: Record<string, unknown>,
	value: unknown,
): string | undefined =>
	Object.keys(object).find((key) => object[key] === value);

export default queryParams;

export const formatCurrency = (
	num: number,
	minFraction?: number,
	maxFraction?: number,
): string => {
	const obj1 = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: minFraction || 0,
		maximumFractionDigits: maxFraction || 0,
	});
	return obj1.format(num);
};
export const getFileTypeFromMIMEType = (
	fileMIMEType: string | undefined,
): string | undefined => fileMIMEType?.split('/')?.[0];

export const copyToClipboard = async (text: string): Promise<void> => {
	try {
		await navigator?.clipboard?.writeText(text);
	} catch {
		// console.log();
	}
};
export const formatRelativeTime = (date: string): string => {
	const now = moment();
	const utcLocalDate = moment.utc(date).local();
	const diffDays = now.diff(utcLocalDate, 'days');

	if (diffDays <= 7) {
		return utcLocalDate.fromNow(); // This will show "4 days ago", "2 days ago" etc
	}
	return utcLocalDate.format('MMM D, YYYY, h:mm A'); // Format: Dec 4, 2024, 8:50 AM
};

export const hasMappingDataRendered = (
	dataSourceLength: number,
	containerId: string,
	hasNextPage: boolean,
): boolean => {
	const element = document?.getElementById(containerId);
	const itemArray = element?.getElementsByClassName('activity-log');
	const isMappingCompleted = (itemArray?.length || 0) >= dataSourceLength;
	return !!(dataSourceLength > 0 && isMappingCompleted && hasNextPage);
};

// Build the `from_date`/`to_date` query params, omitting them entirely when no
// range is set. An unset range means "All Time", so the dates must not be sent
// (sending empty strings would be treated as a real, invalid filter).
export const dateRangeParams = (
	fromDate?: string,
	toDate?: string,
): Record<string, string> =>
	fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {};

export const createAbortSignal = (): AbortController => {
	const controller = new AbortController();
	return controller;
};

export const createAbbreviation = (
	text: string | null | undefined,
	maxChars: number = 2,
): string => {
	if (!text?.trim()) return '';
	const chars = (text.match(/\b\w/g) || []).join('').toUpperCase();
	return maxChars > 0 ? chars.slice(0, maxChars) : chars;
};

export const convertUtcToRelevantTimezone = <T extends string | moment.Moment>(
	dateString: string,
	timezone: string,
	formatTo?: string,
	givenFormat?: MomentBuiltinFormat | string,
): T | boolean => {
	if (dateString && timezone) {
		const formattedUtcTime = moment.utc(dateString, givenFormat);

		if (formattedUtcTime.isValid()) {
			const convertToTimezone = formattedUtcTime?.clone().tz(timezone);

			if (formatTo) {
				return convertToTimezone?.format(formatTo) as T;
			}

			return convertToTimezone as T;
		}
		return false;
	}
	return false;
};

export const categorizeByDate = (
	res: DateEvent[] | unknown,
	timezone: string | undefined,
): Record<string, DateCategory> => {
	if (timezone && Array?.isArray(res)) {
		const formattedData: Record<string, DateCategory> = {};
		((res as DateEvent[]) || [])?.forEach((event) => {
			if (event?.created_at) {
				let createdAt: Moment | null = null;

				const today = moment.tz(timezone).startOf('day');
				const yesterday = moment.tz(timezone).subtract(1, 'day').startOf('day');

				const createdAtInTimeZone = convertUtcToRelevantTimezone(
					event?.created_at,
					timezone,
					'',
					moment.ISO_8601,
				);
				createdAt =
					typeof createdAtInTimeZone !== 'boolean'
						? (createdAtInTimeZone as Moment)
						: null;

				const formattedToday = today?.format('YYYY-MM-DD');
				const formattedYesterday = yesterday?.format('YYYY-MM-DD');
				const formattedCreatedAt = createdAt?.format('YYYY-MM-DD') || '';

				let messageSentDay: string;
				if (formattedToday === formattedCreatedAt) {
					messageSentDay = 'Today';
					formattedData[messageSentDay] = {
						value: messageSentDay,
						content: [
							...(formattedData?.[messageSentDay]?.content || []),
							event,
						],
					};
				} else if (formattedYesterday === formattedCreatedAt) {
					messageSentDay = 'Yesterday';
					formattedData[messageSentDay] = {
						value: messageSentDay,
						content: [
							...(formattedData?.[messageSentDay]?.content || []),
							event,
						],
					};
				} else if (createdAt) {
					const createdAtKey = formattedCreatedAt;
					formattedData[createdAtKey] = {
						value: createdAt?.format('dddd,MMM D'),
						content: [...(formattedData?.[createdAtKey]?.content || []), event],
					};
				}
			}
		});
		return formattedData;
	}
	return {};
};

export const sortDates = (datesArray: string[]): string[] => {
	const toSortArray = datesArray?.filter(
		(item) => item !== 'Today' && item !== 'Yesterday',
	);
	let sortArray: string[] = [];
	if (toSortArray.length) {
		sortArray = orderBy(
			toSortArray,
			(date) => moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD'),
			['desc'],
		);
	}
	if (datesArray?.includes('Yesterday')) {
		sortArray = ['Yesterday', ...sortArray];
	}
	if (datesArray?.includes('Today')) {
		sortArray = ['Today', ...sortArray];
	}

	return sortArray;
};
export const setCookie = (
	name: string,
	value: string,
	days?: number,
	options: CookieOptions = {},
): void => {
	let expires = '';
	if (days) {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		expires = `expires=${date.toUTCString()}`;
	}
	const { path, domain } = options;
	const cookieString = `${name}=${value || ''}${expires ? `; ${expires}` : ''}${
		path ? `; path=${path}` : ''
	}${domain ? `; domain=${domain}` : ''}`;
	document.cookie = cookieString;
};
export const getCookie = (cookieName: string): string | null => {
	const nameEquals = `${cookieName}=`;
	const cookies = document.cookie.split(';');
	for (let i = 0; i < cookies.length; i += 1) {
		const currentCookie = cookies[i].trim();
		if (currentCookie.startsWith(nameEquals)) {
			return currentCookie.substring(nameEquals.length);
		}
	}
	return null;
};
export const getDomain = (): string => {
	const parts = window?.location?.hostname?.split('.');
	if (parts?.length > 2) {
		return parts.slice(-2).join('.');
	}
	return '';
};
export const getToken = (): string | null => {
	const tokenFromCookie = getCookie('customerUserAccess');
	const tokenFromLocalStorage = localStorage.getItem('userAccessToken');
	const isAdminLoginAsCustomer = localStorage.getItem(
		'admin_login_as_customer',
	);
	const token = isAdminLoginAsCustomer
		? tokenFromLocalStorage
		: tokenFromCookie;
	return token;
};

type ErrorInput = string | unknown[] | Record<string, unknown> | null;

export const handleErrorMessages = (
	error: ErrorInput,
	callback: (message: string) => void,
): void => {
	const errorMsg: string[] = [];

	const errorMessage = (err: unknown): void => {
		if (Array.isArray(err)) {
			err.forEach((item: unknown) => {
				errorMessage(item);
			});
		} else if (typeof err === 'object' && err !== null) {
			Object.keys(err as Record<string, unknown>).forEach((item) => {
				errorMessage((err as Record<string, unknown>)[item]);
			});
		} else if (typeof err === 'string') {
			errorMsg.push(err);
		}
	};

	errorMessage(error);

	if (errorMsg?.length > 0) {
		callback(errorMsg.join(', '));
	} else {
		callback('Something went wrong, please try again later.');
	}
};

export const scrollToError = (errors: Record<string, unknown>): void => {
	const firstErrorKey = Object.keys(errors)[0];
	if (!firstErrorKey) return;
	const el = document.querySelector(`[name="${firstErrorKey}"]`);
	if (el) {
		el.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}
};
